import React, { useEffect, useRef, useState } from "react";
import { supabase } from "../supabaseClient";
import { englishMaterialDetail, englishMaterialName, isEnglishLabel } from "../utils/materialNames";
import DuplicateWarning from "./DuplicateWarning";
import { findMaterialDuplicates, readDuplicateMaterials, readDuplicateRows } from "../utils/duplicates";
import { englishError } from "../utils/databaseErrors";

// ======= Popup Modal reusable component =======
function PopupModal({ children, onClose }) {
  return (
    <div className="ui-backdrop"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(81,69,54,0.18)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2001,
      }}
      onClick={onClose}
    >
      <div className="ui-modal ui-modal-small"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#faf8f4",
          borderRadius: 13,
          minWidth: 420,
          maxWidth: "97vw",
          padding: 28,
          boxShadow: "0 0 32px #51453655",
          direction: "ltr",
          maxHeight: "94vh",
          overflowY: "auto",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function MaterialCRUD({ companyCode = "abbas_halim" }) {
  const finished = companyCode === "refaq";
  const [materials, setMaterials] = useState([]);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", color: "", country: "" });
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [dupWarn, setDupWarn] = useState(null); // { matches: [...]} | null
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const displayName = material => finished ? englishMaterialName(material) : material.name;

  useEffect(() => {
    fetchMaterials();
  }, []);

  async function fetchMaterials() {
    try { setMaterials(await readDuplicateRows(supabase, "material", "*")); }
    catch (err) { setError(englishError(err.message)); }
  }

  async function saveMaterial(allowDuplicate = false) {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true); setError("");
    try {
      const values = { name: form.name.trim(), color: form.color.trim(), country: form.country.trim() };
      if (!values.name || (finished && !Object.values(values).every(isEnglishLabel))) {
        setError(finished ? "Enter the material name, color, and country in English." : "Name is required."); return;
      }
      if (!allowDuplicate) {
        const matches = findMaterialDuplicates(values.name, await readDuplicateMaterials(supabase), editId);
        if (matches.length) { setDupWarn({matches: matches.map(material => ({id:material.id,name:displayName(material)}))}); return; }
      }
      const labels = { name_en: values.name, color_en: values.color, country_en: values.country };
      const payload = finished ? (editId ? labels : { ...values, ...labels }) : values;
      const result = editId
        ? await supabase.from("material").update(payload).eq("id", editId)
        : await supabase.from("material").insert([payload]);
      if (result.error) throw result.error;
      setForm({ name: "", color: "", country: "" }); setEditId(null);
      setShowAddPopup(false); setDupWarn(null); await fetchMaterials();
    } catch (err) { setError(englishError(err.message)); setDupWarn(null); }
    finally { busyRef.current = false; setBusy(false); }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    await saveMaterial();
  }

  function handleEdit(mat) {
    const name = displayName(mat);
    const detail = field => finished ? englishMaterialDetail(mat, field) : mat[field] || "";
    setForm({ name: finished && name.includes("(English name needed)") ? "" : name, color: detail("color") === "Not specified" ? "" : detail("color"), country: detail("country") === "Not specified" ? "" : detail("country") });
    setError("");
    setDupWarn(null);
    setEditId(mat.id);
    setShowAddPopup(true);
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this material?")) return;
    await supabase.from("material").delete().eq("id", id);
    fetchMaterials();
  }

  const thStyle = {
    padding: "6px 8px",
    borderBottom: "1.5px solid #d2cbc1",
    background: "#f2ede4",
    fontWeight: "bold",
    fontSize: 13,
    textAlign: "left",
    whiteSpace: "nowrap",
  };

  const tdStyle = {
    padding: "6px 8px",
    fontSize: 13,
    textAlign: "left",
    verticalAlign: "top",
    background: "#faf8f4",
  };

  const btnStyle = {
    padding: "4px 12px",
    borderRadius: 6,
    cursor: "pointer",
    border: "none",
  };

  return (
    <div className="ui-legacy-page"
      dir="ltr"
      style={{
        fontFamily: "Jost, 'Trebuchet MS', Arial, sans-serif",
        background: "#f2ede4",
        minHeight: "100vh",
        maxWidth: 900,
        margin: "auto",
        padding: 24,
        textAlign: "left",
      }}
    >
      <div className="ui-page-header">
      <h2 style={{ color: "var(--accent, #514536)", marginBottom: 20 }}>Materials</h2>

      <button className="ui-primary"
        onClick={() => {
          setEditId(null); setDupWarn(null);
          setError("");
          setForm({ name: "", color: "", country: "" });
          setShowAddPopup(true);
        }}
        style={{
          marginBottom: 20,
          backgroundColor: "var(--accent, #514536)",
          color: "#faf8f4",
          padding: "8px 20px",
          borderRadius: 8,
          border: "none",
          cursor: "pointer",
          fontWeight: "bold",
        }}
      >
        Add material
      </button>
      </div>
      {error && <p role="alert">{error}</p>}

      <div className="ui-table-scroll"><table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          background: "#faf8f4",
          borderRadius: 12,
          boxShadow: "0 1px 10px #51453610",
          fontSize: 13,
          direction: "ltr",
        }}
      >
        <thead>
          <tr style={{ background: "#f2ede4", color: "var(--accent, #514536)" }}>
            <th style={thStyle}>#</th>
            <th style={thStyle}>Name</th>
            <th style={thStyle}>Color</th>
            <th style={thStyle}>Country</th>
            <th style={thStyle}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {materials.map((mat, i) => (
            <tr key={mat.id} style={{ borderBottom: "1px solid var(--line, #d2cbc1)" }}>
              <td style={tdStyle}>{i + 1}</td>
              <td style={tdStyle}>{displayName(mat)}</td>
              <td style={tdStyle}>{finished ? englishMaterialDetail(mat, "color") : mat.color}</td>
              <td style={tdStyle}>{finished ? englishMaterialDetail(mat, "country") : mat.country}</td>
              <td style={{ ...tdStyle, whiteSpace: "nowrap" }}><div className="ui-actions">
                <button className="ui-primary"
                  onClick={() => handleEdit(mat)}
                  style={{
                    ...btnStyle,
                    marginRight: 8,
                    backgroundColor: "var(--accent, #514536)",
                    color: "#faf8f4",
                  }}
                >
                  Edit
                </button>
                <button className="ui-secondary"
                  onClick={() => handleDelete(mat.id)}
                  style={{
                    ...btnStyle,
                    color: "#514536",
                    background: "none",
                  }}
                >
                  Delete
                </button>
              </div></td>
            </tr>
          ))}
          {materials.length === 0 && (
            <tr>
              <td colSpan={5} style={{ textAlign: "center", color: "#716759" }}>
                No materials yet
              </td>
            </tr>
          )}
        </tbody>
      </table></div>

      {showAddPopup && (
        <PopupModal onClose={() => { if (!busy) { setShowAddPopup(false); setDupWarn(null); } }}>
          <h3 style={{ marginTop: 0, marginBottom: 16, color: "var(--accent, #514536)" }}>
            {editId ? "Edit material" : "Add material"}
          </h3>
          <form className="ui-form"
            onSubmit={handleSubmit}
            style={{ display: "flex", flexDirection: "column", gap: 14 }}
          >
            {error && <p role="alert">{error}</p>}
            <label>
              Name
              <input
                required
                disabled={busy || !!dupWarn}
                lang={finished ? "en" : undefined}
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                style={{ width: "100%", padding: 6, fontSize: 14, marginTop: 4 }}
              />
            </label>
            <label>
              Color
              <input
                disabled={busy || !!dupWarn}
                value={form.color}
                onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                style={{ width: "100%", padding: 6, fontSize: 14, marginTop: 4 }}
              />
            </label>
            <label>
              Country
              <input
                disabled={busy || !!dupWarn}
                value={form.country}
                onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
                style={{ width: "100%", padding: 6, fontSize: 14, marginTop: 4 }}
              />
            </label>
            <div className="ui-actions ui-form-actions" style={{ display: "flex", gap: 10, justifyContent: "flex-start" }}>
              <button className="ui-primary"
                type="submit"
                disabled={busy}
                style={{
                  backgroundColor: "var(--accent, #514536)",
                  color: "#faf8f4",
                  padding: "8px 20px",
                  borderRadius: 8,
                  border: "none",
                  cursor: "pointer",
                  fontWeight: "bold",
                }}
              >
                {editId ? "Refresh" : "Add"}
              </button>
              <button className="ui-secondary"
                type="button"
                disabled={busy}
                onClick={() => { setShowAddPopup(false); setDupWarn(null); }}
                style={{
                  backgroundColor: "#d2cbc1",
                  color: "#514536",
                  padding: "8px 20px",
                  borderRadius: 8,
                  border: "none",
                  cursor: "pointer",
                  fontWeight: "bold",
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </PopupModal>
      )}

      {dupWarn && <DuplicateWarning kind="material" matches={dupWarn.matches} busy={busy}
        onCancel={() => setDupWarn(null)} onConfirm={() => saveMaterial(true)} />}
    </div>
  );
}

export default MaterialCRUD;
